import { z } from 'zod';

import { DOCUMENT_DESTINATION_ORDER } from './presentation.js';

/**
 * Catalogue FERMÉ des destinations de déplacement d'une pièce.
 *
 * WHY un catalogue fermé est ici une garde de sécurité, pas du confort de typage :
 * `case_documents.document_category` est une colonne `text` NOT NULL **sans CHECK
 * ni type enum** côté OVH (vérifié 2026-07-17). La base accepte donc n'importe
 * quelle chaîne. Côté UI, il n'existe AUCUN onglet fourre-tout : chaque section
 * du dossier interroge sa catégorie en dur (`NosPiecesSection` → `nos_pieces`,
 * etc.). Conséquence : une pièce écrite avec une catégorie hors catalogue
 * (« Mes pièces » au lieu de `nos_pieces`, une faute de frappe, une hallucination
 * LLM) n'apparaît dans AUCUN onglet — elle est invisible pour l'avocat, sans
 * erreur ni trace. La prod porte déjà les cicatrices de cette absence de garde
 * (`synthesis`, `projets` : des lignes qui ne s'affichent nulle part).
 *
 * Ce catalogue est donc le SEUL rempart entre un agent LLM et une pièce perdue.
 *
 * WHY ces 7 valeurs exactement — c'est le miroir de `MOVABLE_CATEGORY_KEYS`
 * (`MoveToDialog.tsx`), la liste des destinations que l'UI propose à l'avocat.
 * Principe : **la portée de l'agent = la portée de l'avocat**, jamais plus.
 *
 * - `correspondances` en a été EXCLUE tant que `CorrespondancesSection` n'était
 *   monté nulle part : y déplacer une pièce la faisait disparaître de la vue
 *   Gestion. Elle est REVENUE le 2026-09-25, avec le complément Outlook : son
 *   onglet est monté dans `DossierView`, et un courrier versé depuis Outlook y
 *   atterrit. La règle n'a pas changé — une destination n'existe que si une
 *   surface l'affiche ; c'est la surface qui manquait. Toutes les destinations
 *   du catalogue sont proposées à chaque membre : le consentement Microsoft
 *   règle l’accès à la boîte, pas l’accès à une rubrique du dossier.
 *
 * `procedures` (avec S) est un onglet actif et distinct de `procedure` (« Mes
 * écritures ») — cf. le drift de nommage DEBT-030. Il a été ouvert à l'avocat
 * par la PR #608 (« exposer l'onglet Actes de procédure dans la dialog de déplacement »),
 * donc ouvert à l'agent dans le même mouvement (2026-07-20) : la règle est
 * d'ouvrir AUX DEUX d'un coup, sinon le miroir se rompt et l'un des deux
 * chemins perd des pièces.
 *
 * ⚠️ Ne pas confondre avec `DocumentCategorySchema` (`case-wiki/entities.ts`) :
 * celui-là énumère les catégories SOURCE d'un fait wiki (il inclut
 * `correspondances` et ignore `procedures`). Il décrit d'où vient une info, pas
 * où une pièce peut être rangée. Les réutiliser l'un pour l'autre = pièces
 * invisibles.
 */
export const MOVABLE_DOCUMENT_CATEGORIES = DOCUMENT_DESTINATION_ORDER;

export type MovableDocumentCategory = (typeof MOVABLE_DOCUMENT_CATEGORIES)[number];

export const MovableDocumentCategorySchema = z.enum(MOVABLE_DOCUMENT_CATEGORIES);

/**
 * Sous-catégories de `partie_adverse` — les deux « dossiers obligatoires » que
 * `PartieAdverseSection` affiche à la racine de l'onglet Partie adverse.
 *
 * WHY c'est structurant et pas décoratif : depuis la migration 143, ces deux
 * dossiers sont de VRAIES lignes dossier (`document_type = 'system_folder'`) et
 * l'onglet situe une pièce par son `folder_path`, comme partout ailleurs. La
 * sous-catégorie est l'étiquette qui désigne lequel des deux dossiers reçoit la
 * pièce : sans elle, un rangement vers `partie_adverse` n'a pas de destination,
 * d'où son caractère obligatoire. Une pièce étiquetée mais laissée hors de son
 * dossier s'affiche à la racine de l'onglet, à côté d'un dossier compté vide —
 * `move_pieces` résout donc le dossier à partir de l'étiquette.
 */
export const PARTIE_ADVERSE_SUBCATEGORIES = ['actes_communiques', 'pieces_communiquees'] as const;

export type PartieAdverseSubcategory = (typeof PARTIE_ADVERSE_SUBCATEGORIES)[number];

export const PartieAdverseSubcategorySchema = z.enum(PARTIE_ADVERSE_SUBCATEGORIES);

/**
 * Seule catégorie dont la sous-catégorie est structurante (elle décide de
 * l'onglet d'affichage). Ailleurs, `document_subcategory` est un attribut libre
 * (ex. `recherche` → `doctrine` / `analyse` / `decision`) qui ne conditionne
 * aucun affichage : on ne l'écrit pas, on le préserve.
 */
export const CATEGORY_REQUIRING_SUBCATEGORY = 'partie_adverse' satisfies MovableDocumentCategory;

/** Vrai si `value` est une destination de déplacement autorisée. */
export function isMovableDocumentCategory(value: unknown): value is MovableDocumentCategory {
  return (
    typeof value === 'string' &&
    (MOVABLE_DOCUMENT_CATEGORIES as readonly string[]).includes(value)
  );
}

/** Vrai si `value` est une sous-catégorie `partie_adverse` autorisée. */
export function isPartieAdverseSubcategory(value: unknown): value is PartieAdverseSubcategory {
  return (
    typeof value === 'string' &&
    (PARTIE_ADVERSE_SUBCATEGORIES as readonly string[]).includes(value)
  );
}
